/**
 * HoldRecallOrders — Park current sale, recall later.
 * Stored in localStorage for browser POS (no backend needed).
 * Desktop version can later sync to server.
 */
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, Box, Typography, Button, IconButton, Chip, alpha,
} from "@mui/material";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const HOLD_STORAGE_KEY = "pos_held_orders";
const HOLD_SCHEMA_VERSION = 1;
const MAX_HELD_ORDERS = 20;

// ─── Versioned data shape ────────────────────────────────────────────────────

export interface HeldOrder {
  _v: number;            // schema version
  id: string;
  items: any[];          // full CartItem[] snapshot
  customer: any | null;
  discounts: any[];
  totalDiscount: number;
  subTotal: number;
  hstAmount: number;
  grandTotal: number;
  heldAt: string;        // ISO 8601
  note?: string;
}

// ─── Standardized date display ───────────────────────────────────────────────

const formatHeldDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString("en-CA", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const timeAgo = (iso: string): string => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// ─── Storage helpers ─────────────────────────────────────────────────────────

export const getHeldOrders = (): HeldOrder[] => {
  try {
    const raw = localStorage.getItem(HOLD_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as any[];
    // Only return orders matching current schema version
    return parsed.filter((o: any) => o._v === HOLD_SCHEMA_VERSION);
  } catch { return []; }
};

const persistOrders = (orders: HeldOrder[]): void => {
  localStorage.setItem(HOLD_STORAGE_KEY, JSON.stringify(orders));
};

export const saveHeldOrder = (order: Omit<HeldOrder, "id" | "heldAt" | "_v">): HeldOrder => {
  const orders = getHeldOrders();
  const newOrder: HeldOrder = {
    ...order,
    _v: HOLD_SCHEMA_VERSION,
    id: `HOLD-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    heldAt: new Date().toISOString(),
  };
  orders.push(newOrder);
  // Enforce max limit — remove oldest when exceeded
  while (orders.length > MAX_HELD_ORDERS) {
    orders.shift();
  }
  persistOrders(orders);
  return newOrder;
};

export const deleteHeldOrder = (id: string): void => {
  persistOrders(getHeldOrders().filter(o => o.id !== id));
};

export const getHeldOrderCount = (): number => getHeldOrders().length;

// ─── Hold Dialog ─────────────────────────────────────────────────────────────

interface HoldDialogProps {
  open: boolean;
  onClose: () => void;
  onHold: (note: string) => void;
  itemCount: number;
  total: number;
}

export const HoldDialog: React.FC<HoldDialogProps> = ({ open, onClose, onHold, itemCount, total }) => {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <Box sx={{ background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PauseCircleIcon sx={{ fontSize: 22, color: "#fff" }} />
          <Typography fontSize={15} fontWeight={700} color="#fff">Hold Order</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a", mb: 2.5 }}>
          <Box>
            <Typography fontSize={12} color="#92400e" fontWeight={600}>{itemCount} item{itemCount !== 1 ? "s" : ""}</Typography>
            <Typography fontSize={10} color="#b45309">Will be parked for later</Typography>
          </Box>
          <Typography fontSize={20} fontWeight={800} color="#92400e" sx={{ fontFamily: "monospace" }}>${total.toFixed(2)}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75 }}>Note (optional)</Typography>
        <input autoFocus placeholder="e.g. Customer went to grab more items..."
          value={note} onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onHold(note); }}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, outline: "none", boxSizing: "border-box" }}
        />
        <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</Button>
          <Button fullWidth variant="contained" disableElevation onClick={() => onHold(note)}
            startIcon={<PauseCircleIcon sx={{ fontSize: 18 }} />}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, bgcolor: "#d97706", "&:hover": { bgcolor: "#b45309" } }}>
            Hold Order
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ─── Recall Dialog ───────────────────────────────────────────────────────────

interface RecallDialogProps {
  open: boolean;
  onClose: () => void;
  onRecall: (order: HeldOrder) => void;
}

export const RecallDialog: React.FC<RecallDialogProps> = ({ open, onClose, onRecall }) => {
  const [orders, setOrders] = useState<HeldOrder[]>([]);
  useEffect(() => { if (open) setOrders(getHeldOrders()); }, [open]);

  const handleDelete = (id: string) => {
    deleteHeldOrder(id);
    setOrders(getHeldOrders());
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <Box sx={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <PlayCircleIcon sx={{ fontSize: 22, color: "#fff" }} />
          <Typography fontSize={15} fontWeight={700} color="#fff">Held Orders</Typography>
          <Chip label={orders.length} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: "rgba(255,255,255,0.15)", color: "#fff" }} />
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.5)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        {orders.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
            <ShoppingCartIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
            <Typography fontSize={14} color="#94a3b8" fontWeight={500}>No held orders</Typography>
            <Typography fontSize={12} color="#cbd5e1">Orders you park will appear here</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            {orders.map((order, i) => (
              <Box key={order.id} sx={{
                display: "flex", alignItems: "center", gap: 2, px: 3, py: 2,
                borderBottom: i < orders.length - 1 ? "1px solid #f1f5f9" : "none",
                "&:hover": { bgcolor: "#f8fafc" }, transition: "all 0.15s",
              }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha("#f59e0b", 0.08) }}>
                  <PauseCircleIcon sx={{ fontSize: 20, color: "#d97706" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontSize={13} fontWeight={600} color="#0f172a">
                      {order.items.length} item{order.items.length !== 1 ? "s" : ""}
                    </Typography>
                    {order.customer && (
                      <Chip label={order.customer.customerFirstName || "Walk-in"} size="small"
                        sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />
                    )}
                  </Box>
                  {order.note && <Typography fontSize={11} color="#94a3b8" mt={0.25}>"{order.note}"</Typography>}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 11, color: "#cbd5e1" }} />
                    <Typography fontSize={10} color="#cbd5e1">{timeAgo(order.heldAt)} — {formatHeldDate(order.heldAt)}</Typography>
                  </Box>
                </Box>
                <Typography fontSize={16} fontWeight={700} color="#0f172a" sx={{ fontFamily: "monospace", mr: 1 }}>${order.grandTotal.toFixed(2)}</Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Button size="small" variant="contained" disableElevation onClick={() => onRecall(order)}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#059669", minWidth: 60, "&:hover": { bgcolor: "#047857" } }}>
                    Recall
                  </Button>
                  <IconButton size="small" onClick={() => handleDelete(order.id)}
                    sx={{ width: 28, height: 28, "&:hover": { bgcolor: alpha("#ef4444", 0.08) } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
